// loadAttachment.ts
// Загрузка вложения (PDF/Word) с прогрессом, сквозь attachmentCache.
// Единственный внешний импорт общего дерева synced/ — API, тот же специфер,
// что уже использует AnnotationsSidebar.tsx (см. docs/PDFViewer.md).

import { API } from '../../../../../services/base/BaseApi';
import { getCachedFile, putCachedFile } from './attachmentCache';

export interface AttachmentProgress {
    loaded: number;
    total: number | null;
}

export interface LoadAttachmentOptions {
    id: string;
    url: string;
    fallbackType?: string;
    signal?: AbortSignal;
    onCacheMiss?: () => void;
    onProgress?: (progress: AttachmentProgress) => void;
}

export interface LoadAttachmentResult {
    blob: Blob;
    fromCache: boolean;
    size: number;
}

const DEFAULT_TYPE = 'application/octet-stream';

function extractMessageFromBody(body: unknown): string | undefined {
    if (body == null) return undefined;

    if (typeof body === 'string') {
        const trimmed = body.trim();
        if (!trimmed) return undefined;
        try {
            return extractMessageFromBody(JSON.parse(trimmed));
        } catch {
            return trimmed;
        }
    }

    if (typeof body === 'object') {
        const record = body as Record<string, unknown>;
        const candidates = [record.detail, record.title, record.Message, record.message];
        for (const candidate of candidates) {
            if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
        }
    }

    return undefined;
}

function getDefaultDownloadErrorMessage(status?: number): string {
    switch (status) {
        case 401:
            return 'Требуется авторизация';
        case 403:
            return 'Недостаточно прав для скачивания файла';
        case 404:
            return 'Файл не найден';
        case 400:
            return 'Не удалось скачать файл';
        default:
            return status ? `Не удалось скачать файл (код ${status})` : 'Не удалось скачать файл';
    }
}

function decodeArrayBufferBody(data: unknown): unknown {
    if (!(data instanceof ArrayBuffer)) return data;
    try {
        return new TextDecoder().decode(data);
    } catch {
        return undefined;
    }
}

/**
 * Не используем isAxiosError из axios: pda-cfront всё ещё на axios 0.21 (нет
 * cancellation через signal, нет именованных ESM-экспортов вроде isAxiosError —
 * там axios лежит только default-экспортом). Флаг isAxiosError axios ставит на
 * ошибке в любой версии, так что дублируем проверку локально.
 */
function isAxiosErrorLike(error: unknown): error is { response?: { status?: number; data?: unknown } } {
    return typeof error === 'object' && error !== null && (error as { isAxiosError?: unknown }).isAxiosError === true;
}

export function describeDownloadError(error: unknown): string {
    if (isAxiosErrorLike(error) && error.response) {
        const { status, data } = error.response;
        const message = extractMessageFromBody(decodeArrayBufferBody(data));
        return message ?? getDefaultDownloadErrorMessage(status);
    }

    if (error instanceof Error && error.message && !error.message.startsWith('Request failed with status code')) {
        return error.message;
    }

    return getDefaultDownloadErrorMessage();
}

/** Сервер иногда отвечает 200 с телом ошибки (html/json) вместо файла — страхуемся на этот случай. */
function assertNotErrorPayload(data: ArrayBuffer, contentType: string): void {
    const normalized = contentType.toLowerCase();
    if (!normalized.includes('text/html') && !normalized.includes('application/json')) return;

    const text = decodeArrayBufferBody(data);
    const message = typeof text === 'string' ? extractMessageFromBody(text) : undefined;
    throw new Error(message ?? getDefaultDownloadErrorMessage());
}

export async function loadAttachment(options: LoadAttachmentOptions): Promise<LoadAttachmentResult> {
    const { id, url, fallbackType, signal, onCacheMiss, onProgress } = options;

    const cached = await getCachedFile(id);
    if (cached) {
        return { blob: new Blob([cached.buffer], { type: cached.type }), fromCache: true, size: cached.buffer.byteLength };
    }

    onCacheMiss?.();

    // signal через переменную, а не как поле литерала: в axios 0.21 (pda-cfront)
    // AxiosRequestConfig не знает про signal (cancellation появилась в 0.22+) —
    // прямой литерал не пройдёт проверку лишних свойств. В обход неё отмена
    // там просто не сработает и запрос доедет до конца в фоне — не идеально,
    // но безопасно: useAttachmentFile всё равно игнорирует результат после unmount.
    const config: Record<string, unknown> = {
        responseType: 'arraybuffer',
        onDownloadProgress: (event: { loaded: number; total?: number }) => {
            onProgress?.({ loaded: event.loaded, total: event.total ?? null });
        },
    };
    if (signal) config.signal = signal;

    const res = await API.get<ArrayBuffer>(url, config);

    const contentType = `${res.headers['content-type'] ?? fallbackType ?? DEFAULT_TYPE}`;
    assertNotErrorPayload(res.data, contentType);

    const type = contentType.split(';')[0]?.trim() || fallbackType || DEFAULT_TYPE;
    void putCachedFile(id, res.data, type);

    return { blob: new Blob([res.data], { type }), fromCache: false, size: res.data.byteLength };
}
